import { MouseEventHandler, useCallback,useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { GridRowModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
    openDrawer,
    setCurrentDrawerId,
    setCurrentEntity,
    setCurrentOperation,
    setCurrentTenantId
} from 'common/components/EditDrawer/editDrawerSlice';
import { UserProfileEntityContainerWithAccordeon } from 'common/components/UserProfileEntityContainer/UserProfileEntityContainerWithAccordeon';
import { UserProfilesDataGrid } from 'common/components/UserProfilesDataGrid/UserProfilesDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserProfileClaims } from 'common/services/api/UserProfileClaims/UserProfileClaims.thunk';
import {
    ActionDefinition,
    ColumnDefinition,
    ColumnType,
    DrawerEntitiesNames,
    Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';
import { useSnackbar } from 'notistack';

import { UserProfileClaimsDeleteDialog } from './UserProfileClaimsDeleteDialog';
import { UserProfileClaimMembershipCreateContent } from './UserProfileClaimsEditDrawer';
import { selectProcessing, selectUserProfileClaimsByTenantId } from './userProfileClaimsSlice';

const CONTAINER_INLINE_STYLES = {
    boxShadow: 'unset',
    marginBottom: '1rem',
    position: 'unset',
    backgroundColor: 'secondary.light',
    borderRadius: '4px',
    border: 0.5,
    borderColor: 'info.dark',
    flexBasis: '100%',
    marginTop: '24px',
    height: 'max-content'
};

type UserProfileClaimsListProps = {
    tenant: IUserProfileTenant;
};

export function UserProfileClaimsList({ tenant }: Readonly<UserProfileClaimsListProps>) {

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const shouldDisableLinkButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const openButtonDiasbled = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.AppManager, Roles.MultiTenantRO ]);
    const [sortModel, setSortModel] = usePalmSortModel();

    const userProfileClaims = useAppSelector(selectUserProfileClaimsByTenantId(tenant.id));
    const processing = useAppSelector(selectProcessing);
    const navigate = useNavigate();

    const params = useParams();
    useEffect(() => {
        if (userProfileClaims) {
            return;
        }
        const fetchUserProfileClaimsParams = {
            tenantId: tenant.id,
            userId: params?.userId ?? ''
        };
        dispatch(fetchUserProfileClaims(fetchUserProfileClaimsParams))
            .unwrap()
            .catch((error) => {
                handleListFetchError(extractStatusCode(error.message), 'User Profile Claims', enqueueSnackbar);
            });
    }, []);

    const handleCreateButtonClick: MouseEventHandler<HTMLButtonElement> = event => {
        dispatch(setCurrentEntity(DrawerEntitiesNames.UserProfileClaims));
        dispatch(setCurrentOperation(Operations.Link));
        dispatch(setCurrentTenantId(tenant.id));
        dispatch(setCurrentDrawerId(DrawerIDs.Claims));
        dispatch(openDrawer());
        event.stopPropagation();
    };

    const disabledDeleteCallback = (row: GridRowModel) => {
        if (shouldDisableLinkButton) {
            return true;
        }

        if (row.inheritance) {
            return true;
        }

        return false;
    };

    const getRowLink = useCallback((claimId: string, row: GridRowModel | undefined) => {
        if(row?.roleId) {
            return `/${Routes.ApplicationSuites}/${tenant.applicationSuiteId}/${Routes.Tenants}/${tenant.id}/${Routes.Claims}/${row?.claim?.id}/${Routes.Roles}`;
        } else {
            return `/${Routes.ApplicationSuites}/${tenant.applicationSuiteId}/${Routes.Tenants}/${tenant.id}/${Routes.Claims}/${row?.claim?.id}/${Routes.Users}`;
        }
    }, []);

    const openButtonClickHandler = useCallback((claimId: string, row: GridRowModel | undefined) => {
        const link = getRowLink(claimId, row);
        navigate(link);
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: openButtonDiasbled, urlToOpen: getRowLink },
        { action: Operations.Edit, disabled: shouldDisableLinkButton },
        { action: Operations.Delete, disabled: disabledDeleteCallback }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'name', columnName: 'Claim Name', columnType: ColumnType.First, copy: false },
        { columnId: 'claimDataTypeName', columnName: 'Claim Type', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'claimValue', columnName: 'Value', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'inheritance', columnName: 'Inheritance', columnType: ColumnType.Tooltip, copy: false, headerPrompt: 'Inherited items cannot be deleted' },
        { columnId: 'actions', columnName: 'Action', size: 0.4, columnType: ColumnType.Action, sortable: false }
    ];

    return (
        <UserProfileEntityContainerWithAccordeon
            title='Claims'
            isCreateButtonDisabled={shouldDisableLinkButton}
            shouldShowCreateButton={true}
            containerInlineStyles={CONTAINER_INLINE_STYLES}
            createButtonHandler={handleCreateButtonClick}
            operationName={Operations.Link}
        >
            <Box sx={{ margin: '0 24px 24px 24px' }}>
                <UserProfilesDataGrid
                    tenant={tenant}
                    rows={userProfileClaims}
                    actionDefinitions={actionDefinitions}
                    columnDefinitions={columnDefinitions}
                    processing={processing === tenant.id}
                    title='Claim'
                    currentEntityType={DrawerEntitiesNames.UserProfileClaims}
                    contentFunction={UserProfileClaimMembershipCreateContent}
                    deleteFunction={UserProfileClaimsDeleteDialog}
                    customDeleteTitle={'Are you sure you want to remove this Claim from this User?'}
                    rowClick={openButtonDiasbled ? undefined : openButtonClickHandler}
                    rowLink={getRowLink}
                    sortModel={sortModel}
                    clientSortHandler={setSortModel}
                />
            </Box>
        </UserProfileEntityContainerWithAccordeon>
    );
}
